import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Callout, Modal } from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';

/**
 * The inline manager PIN (SCOPE §2-6, "guardrails, not roadblocks").
 *
 * The server already offers this everywhere it matters: a denied action comes
 * back tagged `overridable`, and every gated procedure accepts an `override`.
 * This is the client half — a manager reaches over, picks their name, types
 * four digits, and the action goes through *with their approval recorded on the
 * row*. Nobody signs out, nobody loses the sale, and the audit log knows who
 * said yes.
 *
 * The alternative — "ask a manager to log in" — is how a queue stops.
 */

export interface ManagerOverride {
  readonly employeeId: string;
  readonly pin: string;
  readonly reason?: string;
}

/**
 * Did the server say a manager PIN would unlock this?
 *
 * The API tags every refusal with `overridable`, which is the difference
 * between "you may not do this" and "someone senior may". Call sites use it to
 * put up the prompt instead of a red box.
 */
export function isOverridable(error: unknown): boolean {
  const data = (error as { data?: { overridable?: boolean } } | null)?.data;
  return data?.overridable === true;
}

/**
 * What to tell a manager whose approval just bounced.
 *
 * The two failures carry the same message from the server and differ only in
 * `overridable`: still-unlockable means the PIN did not match, not-unlockable
 * means that person is not allowed to approve this. Without the distinction the
 * manager presses Approve and watches nothing happen.
 */
export function approvalRefusal(error: unknown): string {
  return isOverridable(error)
    ? 'That PIN is not recognized.'
    : 'That person is not allowed to approve this.';
}

export function ManagerApproval({
  title,
  because,
  busy = false,
  error,
  onCancel,
  onApprove,
}: {
  title: string;
  /** What is being approved, in the manager's words, not the system's. */
  because: string;
  busy?: boolean;
  error?: string | null;
  onCancel: () => void;
  onApprove: (override: ManagerOverride) => void;
}) {
  const [employeeId, setEmployeeId] = useState('');
  const [pin, setPin] = useState('');

  const roster = useQuery({
    queryKey: ['auth', 'pinPadRoster'],
    queryFn: () => api.auth.pinPadRoster.query(),
    staleTime: 60_000,
  });

  const ready = employeeId !== '' && /^\d{4,8}$/.test(pin);
  const submit = () => {
    if (ready) onApprove({ employeeId, pin });
  };

  return (
    <Modal
      open
      title={title}
      description={because}
      onClose={onCancel}
      secondaryAction={
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={busy}
          disabledReason={
            employeeId === ''
              ? 'Pick who is approving'
              : /^\d{4,8}$/.test(pin)
                ? undefined
                : 'Enter their 4-digit PIN'
          }
          onClick={submit}
        >
          Approve
        </Button>
      }
    >
      <div className="field">
        <label className="field__label" htmlFor="approver">
          Who is approving?
        </label>
        <select
          id="approver"
          className="select"
          value={employeeId}
          autoFocus
          onChange={(event) => setEmployeeId(event.target.value)}
        >
          <option value="">Pick a name</option>
          {(roster.data ?? []).map((person) => (
            <option key={person.id} value={person.id}>
              {person.displayName}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="approver-pin">
          Their PIN
        </label>
        <input
          id="approver-pin"
          className="input num"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={pin}
          onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 8))}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              submit();
            }
          }}
        />
      </div>

      {roster.isError ? <Callout tone="danger">{errorMessage(roster.error)}</Callout> : null}
      {error != null ? <Callout tone="danger">{error}</Callout> : null}
    </Modal>
  );
}
